// src/utils/exportPDF.js
import { createBrandedPDF, addBrandedTable } from "utils/pdfUtils";
import dayjs from "lib/dayjs";
import autoTable from "jspdf-autotable";
import { getTradeDetailSections } from "pages/TradeTracker/utils/tradeDetails";
import {
  formatNotional,
  wholeUnits,
} from "pages/TradeTracker/utils/formatters";

function pairFields(fields, showMissing = false) {
  const visible = fields.filter((field) => showMissing || field.value != null);
  const rows = [];

  for (let i = 0; i < visible.length; i += 2) {
    const left = visible[i];
    const right = visible[i + 1];
    rows.push([
      left.label,
      left.value ?? "—",
      right?.label || "",
      right ? right.value ?? "—" : "",
    ]);
  }

  return rows;
}

function getChartStatistics(chartData, chartMode) {
  if (chartMode !== "valuation") return null;

  const dataKey = chartMode === "valuation" ? "valuation" : "notional";
  const observations = (chartData || [])
    .map((point) => ({ value: Number(point?.[dataKey]), date: point?.ts }))
    .filter((observation) => Number.isFinite(observation.value));

  if (!observations.length) return null;

  const total = observations.reduce((sum, observation) => sum + observation.value, 0);
  const average = total / observations.length;
  const variance = observations.reduce(
    (sum, observation) => sum + Math.pow(observation.value - average, 2),
    0
  ) / observations.length;

  return {
    max: observations.reduce((current, observation) =>
      observation.value > current.value ? observation : current
    ),
    min: observations.reduce((current, observation) =>
      observation.value < current.value ? observation : current
    ),
    average,
    standardDeviation: Math.sqrt(variance),
  };
}

function formatChartStatistic(value, currencySymbol) {
  const number = Number(value) || 0;
  const absolute = Math.abs(number).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formatted = currencySymbol ? `${currencySymbol}${absolute}` : absolute;
  return number < 0 ? `-${formatted}` : formatted;
}

function addTradeDetailAppendix(doc, trades, valuationByTradeId, selectedDate) {
  for (const [index, trade] of trades.entries()) {
    doc.addPage();

    const tradeId = String(trade.trade_id ?? "").trim();
    const valuation = valuationByTradeId.get(tradeId);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(58, 89, 24);
    doc.text(index === 0 ? "Trade Detail Appendix" : "Trade Detail", 40, 48);

    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Trade ${trade.trade_id || "—"}`, 40, 70);

    let detailY = 84;
    for (const section of getTradeDetailSections(trade, valuation, selectedDate)) {
      const rows = pairFields(section.fields, section.showMissing);
      if (!rows.length) continue;

      autoTable(doc, {
        head: [[{ content: section.title, colSpan: 4 }]],
        body: rows,
        startY: detailY,
        margin: { left: 40, right: 40, bottom: 120 },
        theme: "grid",
        styles: { fontSize: 8.5, cellPadding: 3, valign: "middle" },
        headStyles: {
          fillColor: [94, 140, 62],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
        },
        columnStyles: {
          0: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 78 },
          1: { cellWidth: 168 },
          2: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 78 },
          3: { cellWidth: 168 },
        },
      });

      detailY = (doc.lastAutoTable?.finalY || detailY) + 8;
    }
  }
}

/* --------------------------------------------------------------------------
 * Extract SVG from Recharts chart
 * -------------------------------------------------------------------------- */
function extractChartSVG(plotRef) {
  if (!plotRef?.current) return null;

  const node = plotRef.current;
  let svgEl = null;

  // If the ref points directly to an <svg>, use it
  if (node instanceof SVGSVGElement || node.tagName?.toLowerCase() === "svg") {
    svgEl = node;
  } else {
    // Otherwise, look for a descendant <svg>
    svgEl = node.querySelector("svg");
  }

  if (!svgEl) return null;

  const serializer = new XMLSerializer();
  let svgText = serializer.serializeToString(svgEl);

  // Ensure xmlns exists (some browsers omit it)
  if (!svgText.includes('xmlns="http://www.w3.org/2000/svg"')) {
    svgText = svgText.replace(
      "<svg",
      '<svg xmlns="http://www.w3.org/2000/svg"'
    );
  }

  // Inject a style block for fonts (Helvetica in PDF)
  const firstClose = svgText.indexOf(">");
  if (firstClose !== -1) {
    const styleBlock = `
      <style>
        text, tspan {
          font-family: Helvetica, Arial, sans-serif !important;
        }
      </style>
    `;
    svgText =
      svgText.slice(0, firstClose + 1) +
      styleBlock +
      svgText.slice(firstClose + 1);
  }

  // Replace CSS variable-based strokes/fills with concrete colors
  // so they render correctly when the SVG is isolated in a blob.
  const chartColorMap = {
    "--chart-1": "#3b82f6", // light blue; adjust if you want a different brand color
    "--chart-2": "#22c55e",
    "--chart-3": "#f97316",
  };

  for (const [varName, color] of Object.entries(chartColorMap)) {
    const escaped = varName.replace(/[-]/g, "\\-");
    const strokeRe = new RegExp(`stroke="var\\(${escaped}\\)"`, "g");
    const fillRe = new RegExp(`fill="var\\(${escaped}\\)"`, "g");
    svgText = svgText.replace(strokeRe, `stroke="${color}"`);
    svgText = svgText.replace(fillRe, `fill="${color}"`);
  }

  const { width, height } = svgEl.getBoundingClientRect();
  return { svgText, width: width || 1, height: height || 1 };
}

/* --------------------------------------------------------------------------
 * Convert SVG string → PNG dataURL using offscreen <img> + <canvas>
 * -------------------------------------------------------------------------- */
async function svgToPngDataUrl(svgText, width, height, scale = 2) {
  return new Promise((resolve) => {
    const img = new Image();
    const svgBlob = new Blob([svgText], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");

      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL("image/png");
      resolve(pngUrl);
    };

    img.src = url;
  });
}

/* ==========================================================================
 * MAIN EXPORT FUNCTION
 * ==========================================================================
 */
export default async function exportTradesToPDF({
  filteredTrades,
  valuationByTradeId,
  selectedDate,
  orgName,
  fullUSD,
  createdStamp,
  summary,
  totalNotionalIsCommodity = false,
  plotRef,
  chartData = [],
  chartMode = "valuation",
  notionalAllUSD = false,
}) {
  const safeOrg = (orgName || "Org").replace(/[^A-Za-z0-9]/g, "_");
  const fileName = `TradeTracker_${safeOrg}_${dayjs(selectedDate).format(
    "YYYY-MM-DD"
  )}.pdf`;

  const { doc, addFooter } = createBrandedPDF(
    selectedDate,
    dayjs(createdStamp).format("M/D/YYYY, h:mm A z"),
    orgName || ""
  );

  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 40;
  const usable = pageWidth - 80;

  /* --------------------------------------------------------------------------
   * TRADE PARTY LABEL
   * -------------------------------------------------------------------------- */
  const uniqueParties = Array.from(
    new Set(
      (filteredTrades || [])
        .map((t) => (t?.leg1_party || "").toString().trim())
        .filter(Boolean)
    )
  );

  let tradePartyLabel = "All Trade Parties";
  if (uniqueParties.length === 1) {
    tradePartyLabel = uniqueParties[0];
  } else if (uniqueParties.length === 0) {
    tradePartyLabel = "No Trade Party Selected";
  }

  /* --------------------------------------------------------------------------
   * KPI SECTION
   * -------------------------------------------------------------------------- */
  let y = 130;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(80);
  doc.text(`Portfolio Summary — ${tradePartyLabel}`, left, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  y += 18;
  doc.text(`Total Value: ${fullUSD(summary.totalVal)}`, left, y);

  y += 14;
  const formattedTotalNotional = totalNotionalIsCommodity
    ? wholeUnits(summary.totalNotional)
    : fullUSD(summary.totalNotional);

  doc.text(`Total Notional: ${formattedTotalNotional}`, left, y);

  if (!totalNotionalIsCommodity) {
    y += 14;
    doc.text(`Total PV01: ${fullUSD(summary.totalPv01)}`, left, y);
  }

  /* --------------------------------------------------------------------------
   * TRADE TABLE
   * -------------------------------------------------------------------------- */
  y += 32;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(80);
  doc.text("Trade Details", left, y);

  y += 16;

  const rows = filteredTrades.map((t) => {
    const v = valuationByTradeId.get(String(t.trade_id).trim());
    return [
      t.trade_id || "—",
      t.trade_type || "—",
      t.leg1_party || "—",
      t.leg2_party || "—",
      t.trade_date,
      t.effective_date,
      t.termination_date,
      v?.notional_amount != null
        ? formatNotional(v.notional_amount, t.trade_type)
        : "–",
      v?.total_value != null ? fullUSD(v.total_value) : "–",
    ];
  });

  addBrandedTable(
    doc,
    [
      "Trade ID",
      "Trade Type",
      "Trade Party",
      "Counterparty",
      "Trade Date",
      "Eff Date",
      "Term Date",
      "Notional",
      "Value",
    ],
    rows,
    {
      totalNotional: formattedTotalNotional,
      totalValue: fullUSD(summary.totalVal),
    },
    y
  );

  const tableBottom =
    (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 40;

  /* --------------------------------------------------------------------------
   * CHART EXPORT — SVG → PNG
   * -------------------------------------------------------------------------- */
  const svgObj = extractChartSVG(plotRef);

  if (svgObj) {
    const { svgText, width: svgW, height: svgH } = svgObj;

    const pngDataUrl = await svgToPngDataUrl(svgText, svgW, svgH, 2);

    let chartY = tableBottom + 30;
    const pageHeight = doc.internal.pageSize.getHeight();

    const statistics = getChartStatistics(chartData, chartMode);
    const statsGap = statistics ? 10 : 0;
    const statsW = statistics ? 88 : 0;
    const targetW = usable - statsGap - statsW;
    const scale = targetW / svgW;
    const targetH = svgH * scale;

    if (chartY + targetH + 40 > pageHeight) {
      doc.addPage();
      chartY = 80;
    }

    // Heading above chart
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Historical ${chartMode === "valuation" ? "Value" : "Notional"}`, left, chartY);
    chartY += 16;

    // Chart image
    doc.addImage(pngDataUrl, "PNG", left, chartY, targetW, targetH);

    if (statistics) {
      const statsX = left + targetW + statsGap;
      const statsY = chartY;
      const currencySymbol = chartMode === "valuation" || notionalAllUSD ? "$" : "";
      const rows = [
        [`Max. - ${dayjs(statistics.max.date).format("M/D/YYYY")}`, statistics.max.value],
        [`Min. - ${dayjs(statistics.min.date).format("M/D/YYYY")}`, statistics.min.value],
        ["Average", statistics.average],
        ["Std. Dev.", statistics.standardDeviation],
      ];

      doc.setDrawColor(210);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(statsX, statsY, statsW, targetH, 5, 5, "FD");

      rows.forEach(([label, value], index) => {
        const rowStep = (targetH - 12) / rows.length;
        const rowY = statsY + 11 + index * rowStep;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(110);
        doc.text(label, statsX + 7, rowY);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(55);
        doc.text(formatChartStatistic(value, currencySymbol), statsX + 7, rowY + 9);
      });
    }
  }

  /* --------------------------------------------------------------------------
   * TRADE DETAIL APPENDIX
   * -------------------------------------------------------------------------- */
  addTradeDetailAppendix(
    doc,
    filteredTrades || [],
    valuationByTradeId,
    selectedDate
  );

  /* --------------------------------------------------------------------------
   * FOOTER
   * -------------------------------------------------------------------------- */
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    addFooter(p, totalPages);
  }

  doc.save(fileName);
}
